import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { buildApiUrl, authHeaders } from '../../config/api';

type GraniteKey = 'PJB' | 'MG';
type PlacementKey = 'corner' | 'left' | 'top' | 'special';

interface ArtworkMappingProps {
  onClose: () => void;
}

const toList = (payload: any): any[] => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.artworks)) return payload.artworks;
  return [];
};

const normalizeGranite = (value: any): GraniteKey => {
  const key = String(value || '').toUpperCase();
  if (key === 'MG' || key === 'GREY' || key === 'GRAY' || key.includes('MEDIUM')) return 'MG';
  return 'PJB';
};

const oppositeGranite = (value: GraniteKey): GraniteKey => (value === 'PJB' ? 'MG' : 'PJB');
const PLACEMENTS: PlacementKey[] = ['corner', 'left', 'top', 'special'];

const gridStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: 12,
  maxHeight: 420,
  overflowY: 'auto',
  border: '1px solid #e5e7eb',
  borderRadius: 8,
  padding: 8,
};

const ArtworkMapping: React.FC<ArtworkMappingProps> = ({ onClose }) => {
  const [models, setModels] = useState<any[]>([]);
  const [sizes, setSizes] = useState<any[]>([]);
  const [leftArts, setLeftArts] = useState<any[]>([]);
  const [rightArts, setRightArts] = useState<any[]>([]);
  const [modelId, setModelId] = useState<number>(0);
  const [sizeId, setSizeId] = useState<number>(0);
  const [leftColor, setLeftColor] = useState<GraniteKey>('PJB');
  const [placement, setPlacement] = useState<PlacementKey>('corner');
  const [leftSelectedId, setLeftSelectedId] = useState<number>(0);
  const [rightSelectedId, setRightSelectedId] = useState<number>(0);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState<string>('');

  const rightColor = useMemo(() => oppositeGranite(leftColor), [leftColor]);
  const leftSelectedArt = useMemo(
    () => leftArts.find((a: any) => Number(a?.id) === Number(leftSelectedId)) || null,
    [leftArts, leftSelectedId]
  );

  const loadModels = useCallback(async () => {
    const res = await fetch(buildApiUrl('/api/models'));
    const json = await res.json().catch(() => ({}));
    setModels(toList(json).sort((a: any, b: any) => Number(a?.id || 0) - Number(b?.id || 0)));
  }, []);

  const loadSizes = useCallback(async (nextModelId: number) => {
    if (!nextModelId) {
      setSizes([]);
      return;
    }
    const res = await fetch(buildApiUrl(`/api/models/${nextModelId}/details`));
    const json = await res.json().catch(() => ({}));
    const list = Array.isArray(json?.sizes) ? json.sizes : [];
    setSizes(list);
  }, []);

  const loadArtworks = useCallback(async (mId: number, sId: number, granite: GraniteKey, placementKey: PlacementKey) => {
    if (!mId || !sId) return [];
    const params = new URLSearchParams({
      model_id: String(mId),
      size_id: String(sId),
      granite_type: granite === 'PJB' ? 'PJB' : 'GREY',
      placement: placementKey,
    });
    const res = await fetch(`${buildApiUrl('/api/art')}?${params.toString()}`);
    const json = await res.json().catch(() => ({}));
    return toList(json).filter((a: any) => normalizeGranite(a?.allowed_granite) === granite);
  }, []);

  useEffect(() => {
    loadModels().catch(() => setStatus('Failed to load models'));
  }, [loadModels]);

  useEffect(() => {
    setSizeId(0);
    setLeftSelectedId(0);
    setRightSelectedId(0);
    loadSizes(modelId).catch(() => setStatus('Failed to load sizes'));
  }, [modelId, loadSizes]);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (!modelId || !sizeId) {
        setLeftArts([]);
        setRightArts([]);
        return;
      }
      const [left, right] = await Promise.all([
        loadArtworks(modelId, sizeId, leftColor, placement),
        loadArtworks(modelId, sizeId, rightColor, placement),
      ]);
      if (cancelled) return;
      setLeftArts(left);
      setRightArts(right);
    };
    run().catch(() => setStatus('Failed to load artworks'));
    return () => {
      cancelled = true;
    };
  }, [modelId, sizeId, leftColor, rightColor, placement, loadArtworks]);

  const submit = useCallback(async () => {
    setStatus('');
    if (!modelId || !sizeId || !leftSelectedId || !rightSelectedId) {
      setStatus('Select model, size, and both artworks.');
      return;
    }
    const leftArt = leftArts.find((a: any) => Number(a?.id) === Number(leftSelectedId));
    const rightArt = rightArts.find((a: any) => Number(a?.id) === Number(rightSelectedId));
    if (!leftArt || !rightArt) {
      setStatus('Selected artwork not found. Please reselect.');
      return;
    }
    setSaving(true);
    try {
      const res = await fetch(buildApiUrl('/api/art/mapping'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...authHeaders(),
        },
        body: JSON.stringify({
          model_id: modelId,
          size_id: sizeId,
          placement,
          left_color: leftColor,
          right_color: rightColor,
          left_artwork_id: leftSelectedId,
          right_artwork_id: rightSelectedId,
        }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok || json?.ok === false) {
        throw new Error(json?.error || 'Failed to save mapping');
      }
      setStatus('Artwork mapping saved successfully.');
    } catch (e: any) {
      setStatus(e?.message || 'Failed to save mapping');
    } finally {
      setSaving(false);
    }
  }, [modelId, sizeId, placement, leftSelectedId, rightSelectedId, leftColor, rightColor, leftArts, rightArts]);

  const renderCard = (art: any, selectedId: number, onPick: (id: number) => void) => {
    const isSelected = Number(selectedId) === Number(art?.id);
    return (
      <button
        key={art.id}
        onClick={() => onPick(Number(art.id))}
        style={{
          border: isSelected ? '2px solid #5D722C' : '1px solid #e5e7eb',
          background: '#fff',
          borderRadius: 8,
          padding: 6,
          cursor: 'pointer',
          textAlign: 'left',
        }}
      >
        <img
          src={art.thumb_url || art.image_url || art.thumb_path || ''}
          alt={art.art_code || art.title || 'art'}
          style={{ width: '100%', height: 94, objectFit: 'contain', background: '#f8fafc', borderRadius: 6 }}
        />
        <div style={{ fontSize: 11, fontWeight: 700, marginTop: 4 }}>{art.art_code}</div>
        <div style={{ fontSize: 10, color: '#64748b' }}>{art.title}</div>
        <div style={{ fontSize: 10, color: '#1f2937', marginTop: 2 }}>
          Category: {art.category_name || art.category || `#${art.category_id || '-'}`}
        </div>
      </button>
    );
  };

  return (
    <div>
      <div style={{ display: 'grid', gap: 10, marginBottom: 12 }}>
        <select value={modelId || ''} onChange={(e) => setModelId(Number(e.target.value || 0))}>
          <option value="">Select Model</option>
          {models.map((m: any) => <option key={m.id} value={m.id}>{m.name || m.model_no || `Model ${m.id}`}</option>)}
        </select>
        <select value={sizeId || ''} onChange={(e) => setSizeId(Number(e.target.value || 0))} disabled={!modelId}>
          <option value="">Select Size</option>
          {sizes.map((s: any) => <option key={s.id} value={s.id}>{s.die_size || s.size_type || `Size ${s.id}`}</option>)}
        </select>
        <select value={leftColor} onChange={(e) => setLeftColor(normalizeGranite(e.target.value))}>
          <option value="PJB">PJB</option>
          <option value="MG">MG</option>
        </select>
        <select value={placement} onChange={(e) => setPlacement(String(e.target.value) as PlacementKey)}>
          {PLACEMENTS.map((p) => (
            <option key={p} value={p}>
              {p.charAt(0).toUpperCase() + p.slice(1)}
            </option>
          ))}
        </select>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <div>
          <div style={{ fontWeight: 700, marginBottom: 6 }}>{leftColor} Artwork ({placement})</div>
          <div style={gridStyle}>
            {leftArts.map((a: any) => renderCard(a, leftSelectedId, setLeftSelectedId))}
          </div>
        </div>
        <div>
          <div style={{ fontWeight: 700, marginBottom: 6 }}>{rightColor} Artwork ({placement})</div>
          <div style={gridStyle}>
            {rightArts.map((a: any) => renderCard(a, rightSelectedId, setRightSelectedId))}
          </div>
        </div>
      </div>

      <div style={{ marginTop: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ fontSize: 12, color: status.toLowerCase().includes('failed') ? '#dc2626' : '#16a34a' }}>{status}</div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={onClose}>Close</button>
          <button onClick={submit} disabled={saving}>{saving ? 'Saving...' : 'Submit Mapping'}</button>
        </div>
      </div>
    </div>
  );
};

export default ArtworkMapping;
